import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { AssetCard } from '../components/assets/AssetCard';
import { ImageViewerModal } from '../components/assets/ImageViewerModal';
import { ImageCompareModal } from '../components/assets/ImageCompareModal';
import { AssetTransformationPanel } from '../components/assets/AssetTransformationPanel';
import { DownloadModal } from '../components/assets/DownloadModal';
import { useProcessing } from '../contexts/ProcessingContext';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { uploadConnectorService, UploadedImage, AssetsResponse } from '../services/uploadConnector';
import { reviewService, DownloadStats } from '../services/review';
import { useToast } from '../hooks/use-toast';
import {
  Search,
  Grid3X3,
  List,
  Loader2,
  Upload,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Wand2,
  CheckSquare,
  X,
  FileImage,
  Plus,
  Trash2,
  Package,
  ClipboardCheck,
  SlidersHorizontal,
} from 'lucide-react';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';


interface AssetsFilters {
  batch_id?: string;
  status?: string;
  review_status?: string;
  search?: string;
  page: number;
  limit: number;
}

// Parse filters from URL - outside component to avoid recreation
const parseFiltersFromURL = (searchParams: URLSearchParams): AssetsFilters => ({
  batch_id: searchParams.get('batch') || undefined,
  status: searchParams.get('status') || undefined,
  review_status: searchParams.get('review_status') || undefined,
  search: searchParams.get('search') || undefined,
  page: parseInt(searchParams.get('page') || '1'),
  limit: parseInt(searchParams.get('limit') || '24'),
});

export function AssetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // State - initialize filters once from URL
  const [filters, setFilters] = useState<AssetsFilters>(() => parseFiltersFromURL(searchParams));
  const [assets, setAssets] = useState<UploadedImage[]>([]);
  const [batches, setBatches] = useState<Array<{ id: string; name: string; created_at: string }>>([]);
  const [connectorId, setConnectorId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 24,
    total: 0,
    pages: 0,
  });
  const [viewingAsset, setViewingAsset] = useState<UploadedImage | null>(null);
  const isInitialLoad = useRef(true);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'single' | 'bulk'; imageId?: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create product state
  const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false);
  const [productName, setProductName] = useState('');
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);

  // Transformation panel state
  const [transformationPanelOpen, setTransformationPanelOpen] = useState(false);
  const [singleTransformImage, setSingleTransformImage] = useState<string | null>(null);

  // Compare modal state
  const [comparingAsset, setComparingAsset] = useState<UploadedImage | null>(null);

  // Submit for review state
  const [isSubmittingForReview, setIsSubmittingForReview] = useState(false);
  const [submittingAssetId, setSubmittingAssetId] = useState<string | null>(null);

  // Bulk download state
  const [downloadStats, setDownloadStats] = useState<DownloadStats | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // Local search input (instant typing, debounced API calls)
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show extra filters row when a status/review filter is active or user toggles it
  const [showMoreFilters, setShowMoreFilters] = useState(
    () => !!(filters.status || filters.review_status)
  );

  // Processing context
  const { startProcessing, activeProcessing } = useProcessing();

  // Clean up search debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // Polling for processing assets
  useEffect(() => {
    const hasProcessingAssets = assets.some(asset => asset.status === 'processing');

    if (hasProcessingAssets) {
      const pollInterval = setInterval(() => {
        loadAssets(filters, false);
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(pollInterval);
    }
  }, [assets, filters]);

  // Reload assets when processing completes
  useEffect(() => {
    if (activeProcessing?.status?.status === 'SUCCESS') {
      // Small delay to ensure backend has updated the images
      const timeoutId = setTimeout(() => {
        loadAssets(filters, false);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [activeProcessing?.status?.status]);

  // Update URL when filters change
  const updateURL = useCallback((newFilters: AssetsFilters) => {
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString());
      }
    });
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Load assets function - no dependencies that change frequently
  const loadAssets = useCallback(async (currentFilters: AssetsFilters, showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);

      const response: AssetsResponse = await uploadConnectorService.getAssets({
        page: currentFilters.page,
        limit: currentFilters.limit,
        status: currentFilters.status,
        reviewStatus: currentFilters.review_status,
        batchId: currentFilters.batch_id,
        search: currentFilters.search,
      });

      setAssets(response.assets);
      setBatches(response.batches);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        pages: response.pagination.totalPages,
      });
    } catch (err: any) {
      console.error('Failed to load assets:', err);
      toastRef.current({
        title: 'Error',
        description: 'Failed to load assets',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load - fetch connector, assets, and download stats in parallel
  useEffect(() => {
    const init = async () => {
      const [connector, downloadStatsData] = await Promise.all([
        uploadConnectorService.getDefaultConnector().catch(() => null),
        reviewService.getDownloadStats().catch(() => null),
        loadAssets(filters),
      ]);
      if (connector) setConnectorId(connector.id);
      if (downloadStatsData) setDownloadStats(downloadStatsData);
      isInitialLoad.current = false;
    };
    init();
  }, []); // Only run once on mount

  // Load assets when filters change (but not on initial load)
  useEffect(() => {
    if (!isInitialLoad.current) {
      loadAssets(filters);
    }
  }, [filters, loadAssets]);

  const handleFilterChange = (key: keyof AssetsFilters, value: string | undefined) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    updateURL(newFilters);
    setSelectedAssets(new Set());
    setSelectAllPages(false);
  };

  const handlePageChange = (page: number) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    updateURL(newFilters);
    // Keep selectedAssets across pages — user may pick from multiple pages
    // Only reset selectAllPages since it's a page-level action
    setSelectAllPages(false);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      loadAssets(filters, false),
      reviewService.getDownloadStats().then(setDownloadStats).catch(() => null),
    ]);
    setIsRefreshing(false);
  };

  // Handle download approved images
  const handleDownloadApproved = async () => {
    setIsDownloading(true);
    try {
      const result = await reviewService.bulkDownloadApproved();
      if (result.downloaded > 0) {
        toast({
          title: 'Download Complete',
          description: `Successfully downloaded ZIP with ${result.downloaded} approved image${result.downloaded !== 1 ? 's' : ''}.`,
        });
        const newStats = await reviewService.getDownloadStats();
        setDownloadStats(newStats);
      }
    } catch (error: any) {
      toast({
        title: 'Download Error',
        description: error.message || 'An error occurred during download.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle download all approved images
  const handleDownloadAllApproved = async () => {
    setIsDownloading(true);
    try {
      const result = await reviewService.bulkDownloadAllApproved();
      if (result.downloaded > 0) {
        toast({
          title: 'Download Complete',
          description: `Successfully downloaded ZIP with ${result.downloaded} approved image${result.downloaded !== 1 ? 's' : ''}.`,
        });
        const newStats = await reviewService.getDownloadStats();
        setDownloadStats(newStats);
      }
    } catch (error: any) {
      toast({
        title: 'Download Error',
        description: error.message || 'An error occurred during download.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle download all processed images
  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      const result = await reviewService.bulkDownloadAll();
      if (result.downloaded > 0) {
        toast({
          title: 'Download Complete',
          description: `Successfully downloaded ZIP with ${result.downloaded} image${result.downloaded !== 1 ? 's' : ''}.`,
        });
        const newStats = await reviewService.getDownloadStats();
        setDownloadStats(newStats);
      }
    } catch (error: any) {
      toast({
        title: 'Download Error',
        description: error.message || 'An error occurred during download.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSelectAsset = useCallback((id: string, selected: boolean) => {
    setSelectAllPages(false);
    setSelectedAssets(prev => {
      const newSelected = new Set(prev);
      if (selected) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      return newSelected;
    });
  }, []);

  // Whether every asset on the current page is selected
  const allOnPageSelected = assets.length > 0 && assets.every((a) => selectedAssets.has(a.id));

  const handleSelectAll = () => {
    if (allOnPageSelected) {
      // Deselect only the current page's assets, keep others
      setSelectedAssets(prev => {
        const next = new Set(prev);
        assets.forEach((a) => next.delete(a.id));
        return next;
      });
      setSelectAllPages(false);
    } else {
      // Add current page's assets to existing selection
      setSelectedAssets(prev => {
        const next = new Set(prev);
        assets.forEach((a) => next.add(a.id));
        return next;
      });
    }
  };

  const handleSelectAllPages = () => {
    setSelectAllPages(true);
  };

  const handleClearSelection = () => {
    setSelectedAssets(new Set());
    setSelectAllPages(false);
  };

  const handleDownload = useCallback(async (asset: UploadedImage) => {
    if (!connectorId) return;
    try {
      const result = await uploadConnectorService.getImageDownloadUrl(connectorId, asset.id);
      // Create a temporary link and click it to trigger download
      const link = document.createElement('a');
      link.href = result.url;
      link.download = result.filename || asset.original_filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to download image',
        variant: 'destructive',
      });
    }
  }, [connectorId, toast]);

  const handleDownloadSelected = useCallback(async () => {
    if (!connectorId) return;
    setIsDownloading(true);
    try {
      let result: { url: string; imageCount: number };

      if (selectAllPages) {
        // Download ALL images matching current filters (across all pages)
        result = await uploadConnectorService.downloadByFilters(connectorId, {
          status: filters.status,
          reviewStatus: filters.review_status,
          batchId: filters.batch_id,
          search: filters.search,
        });
      } else if (selectedAssets.size === 1) {
        // Single image — direct download, no ZIP needed
        const imageId = Array.from(selectedAssets)[0];
        const single = await uploadConnectorService.getImageDownloadUrl(connectorId, imageId);
        const response = await fetch(single.url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = single.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        setIsDownloading(false);
        return;
      } else {
        // Multiple selected images on current page
        const imageIds = Array.from(selectedAssets);
        result = await uploadConnectorService.downloadSelectedImages(connectorId, imageIds);
      }

      // Fetch ZIP as blob for proper download
      const response = await fetch(result.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `selected-${result.imageCount}-images.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast({
        title: 'Download Complete',
        description: `ZIP with ${result.imageCount} images downloaded.`,
      });
    } catch (err: any) {
      toast({
        title: 'Download Failed',
        description: err.message || 'Failed to download selected images',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [connectorId, selectedAssets, selectAllPages, filters, toast]);

  const handleView = useCallback((asset: UploadedImage) => {
    setViewingAsset(asset);
  }, []);

  const handleCloseViewer = () => {
    setViewingAsset(null);
  };

  // Delete handlers
  const handleDeleteSingle = useCallback((asset: UploadedImage) => {
    setDeleteTarget({ type: 'single', imageId: asset.id });
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (selectedAssets.size === 0) return;
    setDeleteTarget({ type: 'bulk' });
    setDeleteDialogOpen(true);
  }, [selectedAssets.size]);

  // Transform single image handler
  const handleTransformSingle = useCallback((asset: UploadedImage) => {
    setSingleTransformImage(asset.id);
    setTransformationPanelOpen(true);
  }, []);

  // Compare handler
  const handleCompare = useCallback((asset: UploadedImage) => {
    setComparingAsset(asset);
  }, []);

  // Revert handler
  const handleRevert = useCallback(async (asset: UploadedImage) => {
    if (!connectorId) return;

    try {
      await uploadConnectorService.revertImage(connectorId, asset.id);
      toast({
        title: 'Image Reverted',
        description: 'The image has been reverted to its original state.',
      });
      // Close compare modal if open
      if (comparingAsset?.id === asset.id) {
        setComparingAsset(null);
      }
      // Refresh assets
      loadAssets(filters, false);
    } catch (err: any) {
      toast({
        title: 'Revert Failed',
        description: err.message || 'Failed to revert image',
        variant: 'destructive',
      });
    }
  }, [connectorId, comparingAsset, filters, loadAssets, toast]);

  // Submit for review handler (bulk)
  const handleSubmitForReview = useCallback(async () => {
    if (selectedAssets.size === 0) return;

    // Filter only completed/processed assets
    const processedAssets = assets.filter(
      (a) => selectedAssets.has(a.id) && a.status === 'completed' && a.processed_url
    );

    if (processedAssets.length === 0) {
      toast({
        title: 'No Eligible Assets',
        description: 'Only processed assets can be submitted for review.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmittingForReview(true);
    try {
      const result = await reviewService.submitForReview(processedAssets.map((a) => a.id));

      if (result.submitted > 0) {
        toast({
          title: 'Submitted for Review',
          description: `${result.submitted} asset${result.submitted > 1 ? 's' : ''} submitted for review.${
            result.failed > 0 ? ` ${result.failed} failed.` : ''
          }`,
        });
        setSelectedAssets(new Set());
      } else {
        toast({
          title: 'Submission Failed',
          description: result.errors?.[0]?.reason || 'No assets could be submitted for review.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Submit for review failed:', err);
      toast({
        title: 'Submission Failed',
        description: err.message || 'Failed to submit assets for review.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingForReview(false);
    }
  }, [selectedAssets, assets, toast]);

  // Submit single asset for review handler
  const handleSubmitSingleForReview = useCallback(async (asset: UploadedImage) => {
    if (!asset.processed_url || asset.status !== 'completed') {
      toast({
        title: 'Not Eligible',
        description: 'Only processed assets can be submitted for review.',
        variant: 'destructive',
      });
      return;
    }

    setSubmittingAssetId(asset.id);
    try {
      const result = await reviewService.submitForReview([asset.id]);

      if (result.submitted > 0) {
        toast({
          title: 'Submitted for Review',
          description: `"${asset.original_filename}" submitted for review.`,
        });
        // Refresh to update the UI
        loadAssets(filters, false);
      } else {
        toast({
          title: 'Submission Failed',
          description: result.errors?.[0]?.reason || 'Asset could not be submitted for review.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Submit for review failed:', err);
      toast({
        title: 'Submission Failed',
        description: err.message || 'Failed to submit asset for review.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingAssetId(null);
    }
  }, [toast, loadAssets, filters]);

  const handleConfirmDelete = useCallback(async () => {
    if (!connectorId || !deleteTarget) return;

    setIsDeleting(true);
    try {
      let shouldClearBatchFilter = false;

      if (deleteTarget.type === 'single' && deleteTarget.imageId) {
        const result = await uploadConnectorService.deleteImage(connectorId, deleteTarget.imageId);
        toast({
          title: 'Image Deleted',
          description: 'The image has been permanently deleted.',
        });
        // Check if the batch was deleted (became empty)
        if (result.batchDeleted && result.batchDeleted === filters.batch_id) {
          shouldClearBatchFilter = true;
        }
      } else if (deleteTarget.type === 'bulk') {
        const imageIds = Array.from(selectedAssets);
        const result = await uploadConnectorService.deleteImages(connectorId, imageIds);
        toast({
          title: 'Images Deleted',
          description: `${imageIds.length} images have been permanently deleted.`,
        });
        setSelectedAssets(new Set());
        // Check if the current batch was deleted (became empty)
        if (result.deletedBatches?.includes(filters.batch_id || '')) {
          shouldClearBatchFilter = true;
        }
      }

      // Clear batch filter if needed (this will trigger a refresh via useEffect)
      if (shouldClearBatchFilter) {
        handleFilterChange('batch_id', undefined);
      } else {
        // Only manually refresh if we didn't change filters
        await loadAssets(filters, false);
      }
    } catch (err: any) {
      console.error('Delete failed:', err);
      toast({
        title: 'Delete Failed',
        description: err.message || 'Failed to delete',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  }, [connectorId, deleteTarget, selectedAssets, filters, loadAssets, toast, handleFilterChange]);

  const handleDownloadViewing = async () => {
    if (!viewingAsset || !connectorId) return;
    try {
      const result = await uploadConnectorService.getImageDownloadUrl(connectorId, viewingAsset.id);
      window.open(result.url, '_blank');
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to get download URL',
        variant: 'destructive',
      });
    }
  };

  // Create product handler
  const handleCreateProduct = useCallback(async () => {
    if (!connectorId || selectedAssets.size === 0) return;

    if (!productName.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please enter a product name',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingProduct(true);
    try {
      const imageIds = Array.from(selectedAssets);
      const product = await uploadConnectorService.createProduct(connectorId, {
        imageIds,
        name_en: productName.trim(),
      });

      toast({
        title: 'Product Created',
        description: `Product "${productName}" created with ${imageIds.length} images.`,
      });

      setCreateProductDialogOpen(false);
      setProductName('');
      setSelectedAssets(new Set());

      // Navigate to the product detail page
      navigate(`/upload-products/${product.id}`);
    } catch (err: any) {
      console.error('Create product failed:', err);
      toast({
        title: 'Failed to Create Product',
        description: err.message || 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingProduct(false);
    }
  }, [connectorId, selectedAssets, productName, toast, navigate]);

  const clearFilters = () => {
    const newFilters: AssetsFilters = { page: 1, limit: 24 };
    setSearchInput('');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setFilters(newFilters);
    updateURL(newFilters);
  };

  const hasActiveFilters = filters.batch_id || filters.status || filters.review_status || filters.search;
  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total);

  if (isLoading && assets.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Assets</h1>
          <p className="text-muted-foreground mt-1">
            Manage and process your uploaded images
          </p>
        </div>
        <Button onClick={() => navigate('/uploads')}>
          <Plus className="w-4 h-4 mr-2" />
          Upload More
        </Button>
      </div>

      {/* Filters Bar */}
      <Card className="mb-6">
        <CardContent className="py-4 space-y-3">
          {/* Primary row: Search + Batch + actions */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Search (debounced — types instantly, fires API after 400ms pause) */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by filename..."
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                  searchTimerRef.current = setTimeout(() => {
                    handleFilterChange('search', value || undefined);
                  }, 400);
                }}
                className="pl-10"
              />
            </div>

            {/* Batch Filter */}
            {batches.length > 0 && (
              <Select
                value={filters.batch_id || 'all'}
                onValueChange={(value) => handleFilterChange('batch_id', value === 'all' ? undefined : value)}
              >
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="All Batches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Upload Batch</SelectLabel>
                    <SelectItem value="all">All Batches</SelectItem>
                    {batches
                      .filter((b) => !b.name?.startsWith('Processing '))
                      .map((b) => {
                        const date = new Date(b.created_at);
                        const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        const label = b.name && !b.name.startsWith('Upload ')
                          ? b.name
                          : `Batch ${b.id.slice(0, 6)}`;
                        return (
                          <SelectItem key={b.id} value={b.id}>
                            <span className="flex items-center gap-2">
                              <span className="text-muted-foreground text-xs">{dateStr}</span>
                              <span>{label}</span>
                            </span>
                          </SelectItem>
                        );
                      })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}

            {/* More Filters toggle */}
            <Button
              variant={showMoreFilters ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowMoreFilters(prev => !prev)}
              className="gap-1.5"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {(filters.status || filters.review_status) && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                  {(filters.status ? 1 : 0) + (filters.review_status ? 1 : 0)}
                </Badge>
              )}
            </Button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* View Mode Toggle */}
            <div className="flex items-center border rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-8 px-2"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8 px-2"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>

            {/* Download */}
            <Button
              variant={(downloadStats?.pendingDownload || downloadStats?.totalProcessed) ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDownloadModalOpen(true)}
              disabled={isDownloading || !downloadStats?.totalProcessed}
              className={(downloadStats?.pendingDownload) ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                  {downloadStats?.pendingDownload ? ` (${downloadStats.pendingDownload} new)` : ''}
                </>
              )}
            </Button>

            {/* Refresh */}
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Secondary row: Processing + Review status filters (collapsible) */}
          {showMoreFilters && (
            <div className="flex items-center gap-4 pt-2 border-t">
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Filter by</span>

              {/* Processing Status Filter */}
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
              >
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Processing Status</SelectLabel>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="uploaded">Not Processed</SelectItem>
                    <SelectItem value="processing">Processing...</SelectItem>
                    <SelectItem value="completed">Processed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>

              {/* Review Status Filter */}
              <Select
                value={filters.review_status || 'all'}
                onValueChange={(value) => handleFilterChange('review_status', value === 'all' ? undefined : value)}
              >
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="All Reviews" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Review Status</SelectLabel>
                    <SelectItem value="all">All Reviews</SelectItem>
                    <SelectItem value="none">Not Submitted</SelectItem>
                    <SelectItem value="pending">Pending Review</SelectItem>
                    <SelectItem value="in_review">In Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="revision_requested">Revision Needed</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Selection Bar */}
      {selectedAssets.size > 0 && (
        <Card className="mb-6 border-primary/50 bg-primary/5">
          <CardContent className="py-3">
            {/* "Select all across pages" banner */}
            {allOnPageSelected && pagination.total > assets.length && !selectAllPages && (
              <div className="text-center text-sm mb-3 pb-3 border-b border-primary/20">
                All {assets.length} images on this page are selected.{' '}
                <button
                  className="text-primary font-medium hover:underline"
                  onClick={handleSelectAllPages}
                >
                  Select all {pagination.total} images matching current filters
                </button>
              </div>
            )}
            {selectAllPages && (
              <div className="text-center text-sm mb-3 pb-3 border-b border-primary/20 text-primary font-medium">
                All {pagination.total} images matching current filters are selected.{' '}
                <button
                  className="text-muted-foreground font-normal hover:underline"
                  onClick={handleClearSelection}
                >
                  Clear selection
                </button>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="secondary" className="text-sm">
                  {selectAllPages ? pagination.total : selectedAssets.size} selected
                </Badge>
                <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                  <CheckSquare className="w-4 h-4 mr-1" />
                  {allOnPageSelected ? 'Deselect Page' : 'Select Page'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleClearSelection}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Generate default product name
                    const now = new Date();
                    const timestamp = now.toISOString().replace(/[-:T]/g, '').slice(0, 14);
                    setProductName(`Product-${timestamp}`);
                    setCreateProductDialogOpen(true);
                  }}
                >
                  <Package className="w-4 h-4 mr-1" />
                  Create Product
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadSelected}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-1" />
                  )}
                  {isDownloading ? 'Preparing...' : `Download (${selectAllPages ? pagination.total : selectedAssets.size})`}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setTransformationPanelOpen(true)}
                  disabled={!connectorId}
                >
                  <Wand2 className="w-4 h-4 mr-1" />
                  Process Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-yellow-500 border-yellow-500 hover:bg-yellow-500/10"
                  onClick={handleSubmitForReview}
                  disabled={isSubmittingForReview}
                >
                  {isSubmittingForReview ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <ClipboardCheck className="w-4 h-4 mr-1" />
                  )}
                  Submit for Review
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Summary */}
      {!isLoading && assets.length > 0 && (
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{startItem}-{endItem}</span> of{' '}
            <span className="font-medium text-foreground">{pagination.total}</span> assets
          </div>
        </div>
      )}

      {/* Assets Grid/List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="aspect-[4/3] bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : assets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-muted/50 rounded-full mb-6">
              <FileImage className="h-12 w-12 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl mb-3">No Assets Found</CardTitle>
            <CardDescription className="text-center mb-8 max-w-md">
              {hasActiveFilters
                ? `No assets match your current filters${filters.batch_id && (filters.status || filters.review_status) ? ' — this batch may not have any images with the selected status' : ''}. Try adjusting or clearing your filters.`
                : 'Upload some images to get started with AI processing.'}
            </CardDescription>
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button onClick={() => navigate('/uploads')}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Images
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
              : 'space-y-4'
          }
        >
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              isSelected={selectedAssets.has(asset.id)}
              onSelect={handleSelectAsset}
              onDownload={handleDownload}
              onView={handleView}
              onDelete={handleDeleteSingle}
              onTransform={handleTransformSingle}
              onCompare={handleCompare}
              onRevert={handleRevert}
              onSubmitForReview={handleSubmitSingleForReview}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-1">
            {(() => {
              const current = pagination.page;
              const total = pagination.pages;
              const pages: (number | 'ellipsis')[] = [];

              if (total <= 7) {
                // Show all pages when total is small
                for (let i = 1; i <= total; i++) pages.push(i);
              } else {
                // Always show first page
                pages.push(1);

                // Left ellipsis
                if (current > 3) pages.push('ellipsis');

                // Window around current page
                const start = Math.max(2, current - 1);
                const end = Math.min(total - 1, current + 1);
                for (let i = start; i <= end; i++) pages.push(i);

                // Right ellipsis
                if (current < total - 2) pages.push('ellipsis');

                // Always show last page
                pages.push(total);
              }

              return pages.map((p, idx) =>
                p === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">...</span>
                ) : (
                  <Button
                    key={p}
                    variant={current === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(p)}
                    className="w-8 h-8 p-0"
                  >
                    {p}
                  </Button>
                )
              );
            })()}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={!!viewingAsset}
        imageUrl={viewingAsset?.processed_url || viewingAsset?.original_url || ''}
        filename={viewingAsset?.original_filename || ''}
        onClose={handleCloseViewer}
        onDownload={handleDownloadViewing}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteTarget?.type === 'bulk' ? 'Delete Selected Images' : 'Delete Image'}
        description={
          deleteTarget?.type === 'bulk'
            ? `Are you sure you want to permanently delete ${selectedAssets.size} selected images? This action cannot be undone.`
            : 'Are you sure you want to permanently delete this image? This action cannot be undone.'
        }
        confirmText="Delete"
        loadingText="Deleting..."
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />

      {/* Create Product Dialog */}
      <Dialog open={createProductDialogOpen} onOpenChange={(open) => {
        if (!isCreatingProduct) {
          setCreateProductDialogOpen(open);
          if (!open) setProductName('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Create Product
            </DialogTitle>
            <DialogDescription>
              Group {selectedAssets.size} selected images into a new product. The name will be auto-generated by AI during Studio Retouch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product-name" className="text-muted-foreground">
                Temporary Name <span className="text-xs">(will be replaced by AI)</span>
              </Label>
              <Input
                id="product-name"
                placeholder="Auto-generated..."
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && productName.trim()) {
                    handleCreateProduct();
                  }
                }}
                className="text-muted-foreground"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateProductDialogOpen(false);
                setProductName('');
              }}
              disabled={isCreatingProduct}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProduct}
              disabled={!productName.trim() || isCreatingProduct}
            >
              {isCreatingProduct ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Product'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transformation Panel */}
      {connectorId && (
        <AssetTransformationPanel
          open={transformationPanelOpen}
          onOpenChange={(open) => {
            setTransformationPanelOpen(open);
            if (!open) {
              setSingleTransformImage(null);
            }
          }}
          selectedImageIds={singleTransformImage ? [singleTransformImage] : Array.from(selectedAssets)}
          storeId={connectorId}
          onProcessingStarted={(batchId, taskId, imageCount) => {
            setSingleTransformImage(null);
            setSelectedAssets(new Set());
            // Start processing tracking with floating toast
            if (batchId && taskId) {
              startProcessing(batchId, taskId, imageCount || 0);
            }
            // Refresh assets after short delay to show 'processing' status
            // This ensures the backend has updated statuses and polling kicks in
            setTimeout(() => {
              loadAssets(filters, false);
            }, 500);
          }}
        />
      )}

      {/* Compare Modal */}
      {comparingAsset && comparingAsset.processed_url && (
        <ImageCompareModal
          isOpen={!!comparingAsset}
          originalUrl={comparingAsset.original_url}
          processedUrl={comparingAsset.processed_url}
          filename={comparingAsset.original_filename}
          transformations={comparingAsset.applied_transformations || []}
          onClose={() => setComparingAsset(null)}
          onDownload={(url, type) => {
            // Trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `${type}_${comparingAsset.original_filename}`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          onRevert={() => handleRevert(comparingAsset)}
        />
      )}

      {/* Download Modal */}
      <DownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        downloadStats={downloadStats}
        totalProcessed={downloadStats?.totalProcessed || 0}
        onDownloadApproved={handleDownloadApproved}
        onDownloadAllApproved={handleDownloadAllApproved}
        onDownloadAll={handleDownloadAll}
      />
    </div>
  );
}
